import React, { useState, useEffect } from "react";
import { Psychologist } from "types/psychologist";
import { psychologistService } from "services/psychologists";

interface ProfessionalFormData {
  name: string;
  email: string;
  password?: string;
  phone: string;
  license_id: string;
  address: string;
  biography: string;
}

interface ProfessionalFormProps {
  onClose: () => void;
  onSuccess: () => void;
  professional?: Psychologist | null;
}

const ProfessionalForm: React.FC<ProfessionalFormProps> = ({ onClose, onSuccess, professional }) => {
  const [formData, setFormData] = useState<ProfessionalFormData>({
    name: "",
    email: "",
    password: "",
    phone: "",
    license_id: "",
    address: "",
    biography: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (professional) {
      setFormData({
        name: professional.name,
        email: professional.email,
        phone: professional.psychologist_profile.phone,
        license_id: professional.psychologist_profile.license_id,
        address: professional.psychologist_profile.address,
        biography: professional.psychologist_profile.biography || "",
      });
    }
  }, [professional]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Remove password if it's empty in edit mode
      const submitData = { ...formData };
      if (!submitData.password) {
        delete submitData.password;
      }

      const response = professional 
        ? await psychologistService.update(professional.id, submitData)
        : await psychologistService.create(submitData);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.message || `Failed to ${professional ? "update" : "create"} professional`);
      }
    } catch (err) {
      setError(`An error occurred while ${professional ? "updating" : "creating"} the professional`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 dark:bg-navy-700">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">
            {professional ? "Editar" : "Agregar"} Profesional
          </h3>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Nombre
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>

          {!professional && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Contraseña
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!professional}
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Teléfono
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Número de Licencia
              </label>
              <input
                type="text"
                name="license_id"
                value={formData.license_id}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
              Dirección
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
              Biografía
            </label>
            <textarea
              name="biography"
              value={formData.biography}
              onChange={handleChange}
              required
              rows={4}
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-navy-600 dark:text-white dark:hover:bg-navy-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-70"
            >
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfessionalForm; 